import { wrap, transfer, expose, Remote, createEndpoint } from "comlink"
import { Deferred } from "@/utils/deferred"
import {
	registerPeerForDebugIdSynchronization,
	registerSentryDebugIds,
	removePeerForDebugIdSynchronization,
	synchronizeDebugIdMapWithAllPeers,
} from "@/utils/register-sentry-debug-ids"
import {
	disableSentryLocally,
	initializePeerSentryContext,
	registerPeer,
	setLocalSentryContext,
	unregisterPeer,
} from "@/telemetry/sentry-peers"
import { Sentry } from "@/telemetry/sentry"
import { uuid } from "./uuid"
export type { Remote } from "comlink"
export { transfer } from "comlink"

const WORKER_STARTUP_TIMEOUT_MS = 60_000
const WORKER_RESPONSE_PROBE_TIMEOUT_MS = 5_000

// The worker `error` event carries no response, so re-request the script to record
// what was actually served.
async function describeWorkerResponse(url: string): Promise<Record<string, unknown>> {
	try {
		const response = await fetch(url, {
			signal: AbortSignal.timeout(WORKER_RESPONSE_PROBE_TIMEOUT_MS),
		})
		const contentType = response.headers.get("content-type")
		// Only read non-JavaScript bodies: the failure case is a small HTML
		// document, while a real worker bundle is megabytes.
		const bodyStart = contentType?.includes("javascript")
			? undefined
			: (await response.text()).slice(0, 200)
		return {
			responseStatus: response.status,
			responseContentType: contentType,
			responseCfCacheStatus: response.headers.get("cf-cache-status"),
			responseCfRay: response.headers.get("cf-ray"),
			responseBodyStart: bodyStart,
		}
	} catch (probeError) {
		return { responseProbeFailed: String(probeError) }
	}
}

export interface AnyPeerConnection {
	proxy?: any
}

export interface PeerConsumerOptions {
	dependencies?: { [key: string]: AnyPeerConnection }
	label?: string
}

export class PeerConnection<T extends Peer> {
	public worker?: Worker
	public proxy?: Remote<T>

	public path: string
	public options: PeerConsumerOptions

	public symbol = Symbol()

	private initialized = new Deferred<void>()
	// Detaches the startup listeners and timer once the worker settles, so later
	// runtime errors aren't reported as startup failures.
	private startup?: AbortController

	private get isInsideWorker() {
		if (typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope) {
			return true
		} else {
			return false
		}
	}

	constructor(value: Remote<T> | string, options: PeerConsumerOptions = {}) {
		this.options = options

		if (typeof value === "string") {
			this.path = value
			this.createWorker()
		} else {
			this.path = "default"
			this.proxy = value
		}

		if (!this.isInsideWorker) {
			registerPeer(this)
		}
	}

	terminateWorker() {
		unregisterPeer(this)
		if (this.worker) {
			this.worker.terminate()
			delete this.worker
		}
		removePeerForDebugIdSynchronization(this.symbol)
	}

	// Reset dependencies before their dependents, since dependents read
	// `connection.proxy` synchronously when they spawn.
	async reset(): Promise<void> {
		if (this.isInsideWorker) return

		const oldWorker = this.worker
		removePeerForDebugIdSynchronization(this.symbol)
		delete this.proxy
		if (oldWorker) {
			try {
				oldWorker.terminate()
			} catch {
				/* ignore */
			}
		}
		delete this.worker

		// A terminated worker never fires `error`, so cancel its startup timer
		// instead of letting it report a failure for a worker we killed.
		this.startup?.abort()
		this.initialized = new Deferred<void>()

		await this.createWorker()
		await this.initialized.promise
	}

	public setSentryContext(type: string, data: Record<string, unknown>) {
		if (this.proxy) {
			this.proxy.setSentryContext({ type, data })
		}
	}

	public disableSentry() {
		if (this.proxy) {
			this.proxy.disableSentry()
		}
	}

	async createWorker() {
		if (this.isInsideWorker) {
			return
		}

		let name = this.options.label ?? "Worker"

		if (import.meta.env.PROD) {
			name = uuid()
		}

		this.worker = new Worker(this.path, {
			type: "module",
			name,
		})

		this.proxy = wrap<Peer>(this.worker) as Remote<T>
		initializePeerSentryContext(this)

		// A worker whose script fails to load never posts its init message, so
		// without this `initialized` stays pending and every caller hangs forever.
		const initialized = this.initialized
		const label = this.options.label ?? "Worker"
		// Aborting detaches the listeners and clears the timer, so settling once
		// (either way) guarantees a single report.
		const startup = new AbortController()
		this.startup = startup
		const failStartup = (reason: string) => {
			startup.abort()
			const error = new Error(`Worker "${label}" failed to start: ${reason}`)
			console.error("[Peer]", error.message, this.path)
			void describeWorkerResponse(this.path).then((response) => {
				Sentry.captureException(error, {
					// One issue across workers, so a spike is one climbing group.
					fingerprint: ["worker-startup-failure"],
					tags: { worker_startup_failure: "true", worker_label: label },
					extra: { workerUrl: this.path, reason, ...response },
				})
			})
			initialized.reject(error)
		}
		// Keeps a not-yet-awaited rejection from surfacing as an unhandled
		// rejection; real awaiters still see the error.
		initialized.promise.catch(() => undefined)
		const startupTimer = setTimeout(
			() => failStartup(`no init message within ${WORKER_STARTUP_TIMEOUT_MS}ms`),
			WORKER_STARTUP_TIMEOUT_MS,
		)
		startup.signal.addEventListener("abort", () => clearTimeout(startupTimer), { once: true })
		this.worker.addEventListener(
			"error",
			(event: ErrorEvent) => failStartup(event.message || "script failed to load"),
			{ signal: startup.signal },
		)
		this.worker.addEventListener(
			"messageerror",
			() => failStartup("init message could not be deserialized"),
			{ signal: startup.signal },
		)

		this.worker.addEventListener(
			"message",
			(event: MessageEvent<string>) => {
				const initMessage = event.data
				const sentryDebugIdMapParam = initMessage
					.split("|")
					.find((param) => param.startsWith("SENTRY-DEBUG-ID-MAP->"))
				if (sentryDebugIdMapParam) {
					registerPeerForDebugIdSynchronization({
						symbol: this.symbol,
						peer: this.proxy as Remote<Peer>,
					})
					const stringifiedDebugIdMap = sentryDebugIdMapParam.split("->")[1]
					const sentryDebugIdMap = stringifiedDebugIdMap
						? JSON.parse(stringifiedDebugIdMap)
						: undefined
					if (sentryDebugIdMap && typeof sentryDebugIdMap === "object") {
						const latestDebugIdMapStringified = registerSentryDebugIds(sentryDebugIdMap)
						if (latestDebugIdMapStringified) {
							synchronizeDebugIdMapWithAllPeers(latestDebugIdMapStringified)
						}
					}
				}
				startup.abort()
				console.log("[Peer] Worker initialized", this.options.label)
				this.initialized.resolve()
			},
			{ once: true },
		)

		if (!this.options.dependencies) {
			return
		}

		// We must wait for the worker to be initialized
		// otherwise Comlink.createEndpoint fails
		await this.initialized.promise

		const objects = {}
		const ports: MessagePort[] = []

		for await (const [name, connection] of Object.entries(this.options.dependencies)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			const port: MessagePort = await connection.proxy[createEndpoint]()

			objects[name] = port
			ports.push(port)
		}

		this.proxy?.registerDependencies(transfer(objects, ports)).then(() => {
			this.proxy?.onConnected()
		})
	}

	async connect(): Promise<Remote<T>> {
		await this.initialized.promise

		if (!this.proxy) {
			throw "Invalid connection to non-existent peer"
		}

		// Forwarding Proxy so captured singletons survive a `reset()`.
		const self = this
		return new Proxy({} as Remote<T>, {
			get(_target, prop) {
				if (prop === "then") return undefined
				// Comlink's proxy transferHandler detects marked callbacks via
				// `val[proxyMarker]` (a Symbol). Returning a wrapper for any
				// symbol would make it serialize this Remote through a
				// MessageChannel, and worker-side path traversal would crash.
				if (typeof prop === "symbol") return undefined
				return (...args: unknown[]) => {
					const fetchProxy = async (): Promise<Remote<T>> => {
						if (!self.proxy) await self.initialized.promise
						if (!self.proxy) throw new Error("Peer not connected")
						return self.proxy
					}
					return fetchProxy().then((p) => {
						const member = Reflect.get(p, prop, p) as unknown
						if (typeof member !== "function") return member
						// `member.apply(...)` would access `.apply` via the
						// proxy's get trap and extend the comlink path to
						// `[method, "apply"]`, sending `p` itself as an
						// arg. `Reflect.apply` invokes [[Call]] directly.
						return Reflect.apply(member as (...a: unknown[]) => unknown, p, args)
					})
				}
			},
		})
	}
}

interface PeerDependency {
	port: MessagePort
	remote: Remote<Peer>
}

export class Peer {
	protected dependencies: { [key: string]: PeerDependency } = {}

	public constructor() {
		console.info("Peer", this.constructor.name, "ready.")
	}

	public setSentryContext(context: { type: string; data: Record<string, unknown> }) {
		setLocalSentryContext(context)
		console.info("[Peer]", this.constructor.name, "set Sentry context", context.type)
	}

	public disableSentry() {
		disableSentryLocally()
		console.info("[Peer]", this.constructor.name, "disabled Sentry")
	}

	public static broadcast() {
		expose(new this())
		let initMessage = "INIT"
		const sentryDebugIdMap = (self as any)._sentryDebugIds
		if (sentryDebugIdMap && typeof sentryDebugIdMap === "object") {
			initMessage += `|SENTRY-DEBUG-ID-MAP->${JSON.stringify(sentryDebugIdMap)}`
		}
		postMessage(initMessage)
	}

	public onConnected() {
		// Implemented by subclasses.
	}

	public updateDebugIdMap(debugIdMapStringified: string) {
		const debugIdMap = JSON.parse(debugIdMapStringified)
		if (typeof debugIdMap === "object") {
			registerSentryDebugIds(debugIdMap)
		}
	}

	protected getDependencyPort(key: string): MessagePort {
		const port = this.dependencies[key]?.port

		if (!port) {
			throw `[Peer] Missing dependency port ${key}.`
		}

		return port
	}

	protected getDependency<T extends Peer>(key: string): Remote<T> {
		const dependency = this.dependencies[key]?.remote as Remote<T>

		if (!dependency) {
			throw `[Peer] Missing dependency ${key}.`
		}

		return dependency
	}

	public registerDependencies(dependencies: { [key: string]: MessagePort }) {
		for (const [key, port] of Object.entries(dependencies)) {
			this.dependencies[key] = { remote: wrap<Peer>(port), port }
			console.log("[Peer] Dependency registered", key)
		}
	}
}
